import { useTranslation } from '@rhino-project/core/i18n';
import { lazy, Suspense, useCallback, useMemo, useState } from 'react';
import { AlertDialog, Button, Card, Spinner } from '@heroui/react';
import { getModel } from '@rhino-project/core/utils';
import { BaseAuthedPage, MaxWidth } from '@rhino-project/ui-heroui';
import { Icon } from '@iconify/react';
import {
  modelKey,
  useModelDelete,
  useModelIndex,
  useModelShow
} from '@rhino-project/core/hooks';
import { useQueryClient } from '@tanstack/react-query';
import { useHasRoleOf } from '@rhino-project/core/hooks';
import ContactSidebarForm from '../../components/contact/ContactSidebarForm';
import ReportSidebarForm from '../../components/report/ReportSidebarForm';
import ReportListItem from '../../components/report/ReportListItem';
import RosterSidebarForm from '../../components/roster/RosterSidebarForm';
import PlayerRosterStatuses from '../../components/players/PlayerRosterStatus';
import PlayerIndexSection from '../../components/players/PlayerIndexSection';
import { Link, notFound, useNavigate, useParams } from '@tanstack/react-router';
import {
  InlineEditChip,
  HeightChip,
  PositionsChip,
  RatingChip,
  CountryChip
} from '../../components/shared/PlayerChips';
import InlineEditHeading from '../../components/shared/InlineEditHeading';
import InlineNoteForm from '../../components/shared/InlineNoteForm';
import { useActiveSeasonWindow } from '../../hooks/ots';
import type { RhinoRead, RhinoRecordIdentifier } from '@rhino-project/core';
import EventListItem from '../../components/event/EventListItem';
import NoteListItem from '../../components/note/NoteListItem';

// Charts pull in Recharts (~150kB gzipped); load it only when a player page is
// viewed rather than in the main app bundle.
const PlayerRatingChart = lazy(
  () => import('../../components/charts/PlayerRatingChart')
);
const PlayerRadarChart = lazy(
  () => import('../../components/charts/PlayerRadarChart')
);

interface ContactItemProps {
  contact: RhinoRead<'contact'>;
  onEdit: (contact: RhinoRead<'contact'>) => void;
  isAdmin?: boolean;
}

const ContactItem = ({ contact, onEdit, isAdmin }: ContactItemProps) => {
  const { t } = useTranslation();

  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const { mutate: destroyContact, isPending: isDeleting } =
    useModelDelete('contact');

  const handleDelete = useCallback(() => {
    destroyContact(contact.id, {
      onSuccess: () => setIsDeleteOpen(false)
    });
  }, [destroyContact, contact.id]);

  return (
    <div className="py-2 border-b border-separator last:border-b-0">
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-1.5">
            <span className="font-medium">{contact.display_name}</span>
            <span className="text-muted text-sm">({contact.relationship})</span>
            {!!contact.primary && (
              <span className="text-xs bg-accent/15 text-accent px-1.5 py-0.5 rounded-full">
                {t('frontend.messages.primary')}
              </span>
            )}
          </div>
          <div className="flex gap-3 mt-1 text-sm">
            {!!contact.email && (
              <a
                href={`mailto:${contact.email}`}
                className="text-accent hover:underline"
              >
                {contact.email}
              </a>
            )}
            {!!contact.phone && (
              <a
                href={`tel:${contact.phone}`}
                className="text-accent hover:underline"
              >
                {contact.phone}
              </a>
            )}
          </div>
          {!!contact.note && (
            <div className="text-muted text-sm mt-1">{contact.note}</div>
          )}
        </div>
        {isAdmin && (
          <div className="flex gap-1 ml-2">
            <Button
              isIconOnly
              variant="ghost"
              size="sm"
              aria-label={t('frontend.messages.edit_contact')}
              onPress={() => onEdit(contact)}
              className="text-muted hover:text-foreground"
            >
              <Icon icon="bi:pencil" width="14" />
            </Button>
            <AlertDialog isOpen={isDeleteOpen} onOpenChange={setIsDeleteOpen}>
              <Button
                isIconOnly
                variant="ghost"
                size="sm"
                aria-label={t('frontend.messages.delete_contact')}
                className="text-muted hover:text-danger"
              >
                <Icon icon="bi:trash" width="14" />
              </Button>
              <AlertDialog.Backdrop>
                <AlertDialog.Container size="sm">
                  <AlertDialog.Dialog>
                    <AlertDialog.CloseTrigger />
                    <AlertDialog.Header>
                      <AlertDialog.Icon status="danger" />
                      <AlertDialog.Heading>
                        {t('frontend.messages.delete_contact_2')}
                      </AlertDialog.Heading>
                    </AlertDialog.Header>
                    <AlertDialog.Body>
                      {t('frontend.messages.delete_confirmation', {
                        name: contact.display_name
                      })}
                    </AlertDialog.Body>
                    <AlertDialog.Footer>
                      <Button
                        variant="ghost"
                        onPress={() => setIsDeleteOpen(false)}
                      >
                        {t('frontend.messages.cancel')}
                      </Button>
                      <Button
                        variant="danger"
                        onPress={handleDelete}
                        isDisabled={isDeleting}
                      >
                        {isDeleting
                          ? t('frontend.messages.deleting')
                          : t('frontend.messages.delete_contact_2')}
                      </Button>
                    </AlertDialog.Footer>
                  </AlertDialog.Dialog>
                </AlertDialog.Container>
              </AlertDialog.Backdrop>
            </AlertDialog>
          </div>
        )}
      </div>
    </div>
  );
};

interface ModalInfo {
  open: string | false;
  action?: string;
  resource?:
    RhinoRead<'contact'> | { player?: RhinoRecordIdentifier; id?: undefined };
}

const SeasonPlayerPage = () => {
  const { t } = useTranslation();

  const [showNoteForm, setShowNoteForm] = useState(false);
  const [modalInfo, setModalInfo] = useState<ModalInfo>({
    open: false,
    action: 'create',
    resource: {}
  });
  const queryClient = useQueryClient();
  const { owner, id: playerId, team_season_id } = useParams({ strict: false });
  const { window: seasonWindow, isLoading: isWindowLoading } =
    useActiveSeasonWindow(team_season_id);
  const modalKey = useMemo(() => {
    if (!modalInfo.open) return null;

    return modelKey(getModel(modalInfo.open), 'index');
  }, [modalInfo.open]);
  const isAdmin = useHasRoleOf('admin');
  const navigate = useNavigate();
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const { mutate: destroyPlayer, isPending: isDeleting } =
    useModelDelete('player');

  const {
    isLoading,
    resource: player,
    refetch
  } = useModelShow('player', playerId!);

  const p = player;

  // Fetch in-window reports with their values — used for both the hasReports
  // gate and the season-scoped rating chip.  filter/order/limit match the chart
  // components exactly so React Query deduplicates the underlying request.
  const { resources: seasonReportsRes } = useModelIndex('report', {
    filter: {
      player_id: playerId,
      ...(seasonWindow ? { reported_at: seasonWindow } : {})
    },
    order: ['reported_at'],
    limit: 100
  });
  const seasonReports = seasonReportsRes;
  const hasReports = (seasonReports?.total ?? 0) > 0;

  // Average of all "Rating" report_values within the season window,
  // mirroring the backend quick_rating computation but scoped to the season.
  const seasonRating = useMemo<number | null>(() => {
    const vals: number[] = [];
    for (const report of seasonReports?.results ?? []) {
      for (const v of report.report_values ?? []) {
        const field = v.report_field;
        if (field?.name !== 'Rating') continue;
        const n = parseFloat(v.value as string);
        if (!Number.isNaN(n)) vals.push(n);
      }
    }
    if (vals.length === 0) return null;
    return (
      Math.round((vals.reduce((a, b) => a + b, 0) / vals.length) * 10) / 10
    );
  }, [seasonReports]);

  const handleAdd = (type: string) =>
    setModalInfo({
      open: type,
      action: 'create',
      resource: { player: playerId }
    });

  const handleEdit = (type: string, resource: RhinoRead<'contact'>) =>
    setModalInfo({ open: type, action: 'update', resource });

  const handleModalClose = () => {
    // Invalidate the model we just opened the dialog for
    if (modalKey) queryClient.invalidateQueries({ queryKey: [modalKey] });

    // Report can change the rating so refresh the player
    if (modalInfo.open === 'report') refetch();

    setModalInfo({ open: false });
  };

  const handleDeletePlayer = useCallback(() => {
    if (!p) return;

    destroyPlayer(p.id, {
      onSuccess: () => {
        setIsDeleteOpen(false);
        if (team_season_id) {
          navigate({
            to: '/$owner/active/$team_season_id/players',
            params: { owner: owner!, team_season_id }
          });
        } else {
          navigate({ to: '/$owner/players', params: { owner: owner! } });
        }
      }
    });
  }, [destroyPlayer, p, navigate, owner, team_season_id]);

  const playerResource = useMemo(() => ({ player: p?.id }), [p]);

  if (isLoading || isWindowLoading) {
    return <Spinner size="sm" className="mx-auto mt-8 block" />;
  }

  if (!p) throw notFound();

  const noteFilter = {
    player_id: p!.id,
    ...(seasonWindow ? { created_at: seasonWindow } : {})
  };
  const eventFilter = {
    player_id: p!.id,
    ...(seasonWindow ? { created_at: seasonWindow } : {})
  };
  const reportFilter = {
    player_id: p!.id,
    ...(seasonWindow ? { reported_at: seasonWindow } : {})
  };

  return (
    <BaseAuthedPage>
      <MaxWidth>
        <div className="flex flex-row justify-between items-start">
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-3">
              <InlineEditHeading
                player={
                  p as
                    | {
                        id: number | string;
                        first_name?: string | null;
                        last_name?: string | null;
                        number?: string | null;
                      }
                    | undefined
                }
                isAdmin={isAdmin}
                onSaved={refetch}
              />
              <RatingChip rating={seasonRating} />
            </div>
            <div className="flex flex-wrap gap-1.5 items-center">
              <PositionsChip player={p} isAdmin={isAdmin} onSaved={refetch} />
              <InlineEditChip
                value={p?.hand}
                label={t('frontend.messages.hand')}
                field="hand"
                playerId={p?.id}
                isAdmin={isAdmin}
                onSaved={refetch}
                options={[
                  { value: 'L', label: t('frontend.messages.left') },
                  { value: 'R', label: t('frontend.messages.right') }
                ]}
              />
              <InlineEditChip
                value={p?.dob_year}
                label={t('frontend.messages.born')}
                field="dob_year"
                playerId={p?.id}
                type="number"
                isAdmin={isAdmin}
                onSaved={refetch}
              />
              <InlineEditChip
                value={p?.grad_year}
                label={t('frontend.messages.grad')}
                field="grad_year"
                playerId={p?.id}
                type="number"
                isAdmin={isAdmin}
                onSaved={refetch}
              />
              <HeightChip
                heightFeet={p?.height_feet}
                heightInches={p?.height_inches}
                playerId={p?.id}
                isAdmin={isAdmin}
                onSaved={refetch}
              />
              <InlineEditChip
                value={p?.weight_lbs ? `${p.weight_lbs}` : null}
                label={t('frontend.messages.weight')}
                field="weight_lbs"
                playerId={p?.id}
                type="number"
                isAdmin={isAdmin}
                onSaved={refetch}
              />
              <InlineEditChip
                value={p?.hometown}
                label={t('frontend.messages.hometown')}
                field="hometown"
                playerId={p?.id}
                isAdmin={isAdmin}
                onSaved={refetch}
              />
              <CountryChip
                country={p?.country}
                playerId={p?.id}
                isAdmin={isAdmin}
                onSaved={refetch}
              />
            </div>
            <div className="flex items-center gap-2 text-sm">
              <span className="text-muted">
                {t('frontend.messages.current_season_only')}
              </span>
              <Link
                to="/$owner/players/$id"
                params={{ owner: owner!, id: String(p!.id) }}
                className="flex items-center gap-1 hover:underline w-fit"
              >
                {t('frontend.messages.all_historical_data')}
                <Icon icon="bi:arrow-right" />
              </Link>
            </div>
          </div>
          {isAdmin && (
            <AlertDialog isOpen={isDeleteOpen} onOpenChange={setIsDeleteOpen}>
              <Button variant="ghost" size="sm" className="text-danger">
                <Icon icon="bi:trash" />
                {t('frontend.messages.delete')}
              </Button>
              <AlertDialog.Backdrop>
                <AlertDialog.Container size="sm">
                  <AlertDialog.Dialog>
                    <AlertDialog.CloseTrigger />
                    <AlertDialog.Header>
                      <AlertDialog.Icon status="danger" />
                      <AlertDialog.Heading>
                        {t('frontend.messages.delete_player')}
                      </AlertDialog.Heading>
                    </AlertDialog.Header>
                    <AlertDialog.Body>
                      {t('frontend.messages.delete_player_confirmation', {
                        name: p?.display_name
                      })}
                    </AlertDialog.Body>
                    <AlertDialog.Footer>
                      <Button
                        variant="ghost"
                        onPress={() => setIsDeleteOpen(false)}
                      >
                        {t('frontend.messages.cancel')}
                      </Button>
                      <Button
                        variant="danger"
                        onPress={handleDeletePlayer}
                        isDisabled={isDeleting}
                      >
                        {isDeleting
                          ? t('frontend.messages.deleting')
                          : t('frontend.messages.delete_player')}
                      </Button>
                    </AlertDialog.Footer>
                  </AlertDialog.Dialog>
                </AlertDialog.Container>
              </AlertDialog.Backdrop>
            </AlertDialog>
          )}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
          {/* Left column -- primary content */}
          <div className="lg:col-span-8">
            {hasReports && (
              <Suspense
                fallback={
                  <Card className="my-2">
                    <Card.Content>
                      <Spinner size="sm" className="mx-auto my-8 block" />
                    </Card.Content>
                  </Card>
                }
              >
                <div className="grid grid-cols-1 gap-2 xl:grid-cols-2">
                  {/* Use the route `playerId` (not `p.id`) so these report
                      queries share React Query's cache key with the rating
                      query above, which also filters by the string param. */}
                  <PlayerRatingChart
                    playerId={playerId!}
                    reportedAtWindow={seasonWindow}
                  />
                  <PlayerRadarChart
                    playerId={playerId!}
                    reportedAtWindow={seasonWindow}
                  />
                </div>
              </Suspense>
            )}
            <PlayerIndexSection
              key="note"
              baseModel="note"
              player={p!}
              filter={noteFilter}
              title={
                <span className="flex items-center gap-2">
                  {t('frontend.messages.notes')}
                  {isAdmin && !showNoteForm && (
                    <Button
                      isIconOnly
                      variant="ghost"
                      size="sm"
                      aria-label={t('frontend.messages.add_note')}
                      onPress={() => setShowNoteForm(true)}
                    >
                      <Icon icon="bi:plus" />
                    </Button>
                  )}
                </span>
              }
              emptyMessage={t('frontend.messages.no_notes_yet')}
              before={
                showNoteForm ? (
                  <div className="mb-2">
                    <InlineNoteForm
                      playerId={p?.id}
                      onSave={() => setShowNoteForm(false)}
                    />
                  </div>
                ) : undefined
              }
              render={(note) => <NoteListItem key={note.id} note={note} />}
            />
            <PlayerIndexSection
              key="report"
              baseModel="report"
              order={['-reported_at', '-created_at']}
              player={p!}
              filter={reportFilter}
              title={
                <span className="flex items-center gap-2">
                  {t('frontend.messages.reports')}
                  {isAdmin && (
                    <Button
                      isIconOnly
                      variant="ghost"
                      size="sm"
                      aria-label={t('frontend.messages.add_report')}
                      onPress={() => handleAdd('report')}
                    >
                      <Icon icon="bi:plus" />
                    </Button>
                  )}
                </span>
              }
              emptyMessage={t('frontend.messages.no_reports_yet')}
              render={(report) => (
                <ReportListItem
                  key={report.id}
                  report={report}
                  owner={owner!}
                />
              )}
            />
          </div>

          {/* Right column -- reference info */}
          <div className="lg:col-span-4">
            <Card className="my-2">
              <Card.Header className="flex-row justify-between items-center">
                <Card.Title>
                  <span className="flex items-center gap-2">
                    {t('frontend.messages.roster_status')}
                    {isAdmin && (
                      <Button
                        isIconOnly
                        variant="ghost"
                        size="sm"
                        aria-label={t('frontend.messages.add_roster_status')}
                        onPress={() => handleAdd('roster')}
                      >
                        <Icon icon="bi:plus" />
                      </Button>
                    )}
                  </span>
                </Card.Title>
              </Card.Header>
              <Card.Content>
                <PlayerRosterStatuses player={p!} />
              </Card.Content>
            </Card>
            <PlayerIndexSection
              key="contact"
              baseModel="contact"
              order={['-player_contact', '-primary', '-created_at']}
              player={p!}
              title={
                <span className="flex items-center gap-2">
                  {t('frontend.messages.contacts')}
                  {isAdmin && (
                    <Button
                      isIconOnly
                      variant="ghost"
                      size="sm"
                      aria-label={t('frontend.messages.add_contact')}
                      onPress={() => handleAdd('contact')}
                    >
                      <Icon icon="bi:plus" />
                    </Button>
                  )}
                </span>
              }
              emptyMessage={t('frontend.messages.no_contacts_yet')}
              render={(contactItem) => {
                return (
                  <ContactItem
                    key={contactItem.id}
                    contact={contactItem}
                    onEdit={(c) => handleEdit('contact', c)}
                    isAdmin={isAdmin}
                  />
                );
              }}
            />
            <PlayerIndexSection
              key="event"
              baseModel="event"
              player={p!}
              filter={eventFilter}
              title={t('frontend.messages.history')}
              render={(event) => <EventListItem key={event.id} event={event} />}
            />
          </div>
        </div>
      </MaxWidth>
      <ContactSidebarForm
        isOpen={modalInfo.open === 'contact'}
        action={modalInfo.action}
        parentId={p?.id}
        modelId={modalInfo.resource?.id}
        onModalClose={handleModalClose}
      />
      <RosterSidebarForm
        resource={playerResource}
        isOpen={modalInfo.open === 'roster'}
        parentId={p?.id}
        onModalClose={handleModalClose}
      />
      <ReportSidebarForm
        resource={playerResource}
        isOpen={modalInfo.open === 'report'}
        parentId={p?.id}
        onModalClose={handleModalClose}
      />
    </BaseAuthedPage>
  );
};

export default SeasonPlayerPage;
